import { api } from '../../api/client.js';
import { 
  POLLING_TYPES, 
  PROJECT_PROCESSING_STATUSES,
  SEGMENT_PROCESSING_STATUSES,
  PROJECT_FINAL_STATUSES,
  SEGMENT_FINAL_EXTRACT_STATUSES,
  SEGMENT_FINAL_RENDER_STATUSES
} from './types';
import { notifySubscribers } from './subscribers';

/**
 * Poll for status of an extraction item
 * @param {string} itemId ID of the extraction
 * @param {string} projectId ID of the project
 * @param {Map} statuses Current status map
 * @returns {Object} Updated status and completion info
 */
export const pollExtraction = async (itemId, projectId, statuses) => {
  if (!projectId) {
    throw new Error('Missing projectId for extraction polling');
  }
  
  const response = await api.getShort(projectId, itemId);
  const currentStatus = statuses.get(itemId);
  
  // Check if status or associated data actually changed
  const updated = currentStatus?.status !== response.status || 
                  currentStatus?.progress !== response.progress;
  
  const status = {
    status: response.status,
    progress: response.progress || 0,
    current_step: response.current_step || 'Processing...',
    data: response
  };
  
  // Determine if item is complete
  const isComplete = !SEGMENT_PROCESSING_STATUSES.includes(response.status);
  
  // Notify subscribers if updated
  if (updated) {
    const subscriberStatus = {
      status: response.status,
      progress: response.progress || 0,
      current_step: response.current_step || 'Processing...',
      data: response  // Include full response for video URLs and other data
    };
    notifySubscribers(itemId, subscriberStatus);
  }
  
  return { 
    status, 
    updated, 
    isComplete, 
    finalStatus: response.status 
  };
};

/**
 * Poll for status of a render item
 * @param {string} itemId ID of the render
 * @param {string} projectId ID of the project
 * @param {Map} statuses Current status map
 * @returns {Object} Updated status and completion info
 */
export const pollRender = async (itemId, projectId, statuses) => {
  if (!projectId) {
    throw new Error('Missing projectId for render polling');
  }
  
  const response = await api.getShortDetails(projectId, itemId);
  const currentStatus = statuses.get(itemId);
  
  // Check if render status or associated data actually changed
  const updated = currentStatus?.status !== response.status || 
                  currentStatus?.progress !== response.progress;
  
  const status = {
    status: response.status,
    progress: response.progress || 0,
    current_step: response.current_step || 'Rendering...',
    data: response
  };
  
  // Determine if item is complete
  const isComplete = response.status && SEGMENT_FINAL_RENDER_STATUSES.includes(response.status.toUpperCase());
  
  // Notify subscribers if updated
  if (updated) {
    const subscriberStatus = {
      status: response.status,
      progress: response.progress || 0,
      current_step: response.current_step || 'Rendering...',
      data: response  // Include full response for video URLs and other data
    };
    notifySubscribers(itemId, subscriberStatus);
  }
  
  return { 
    status, 
    updated, 
    isComplete, 
    finalStatus: response.status 
  };
};

/**
 * Poll for status of a project item
 * @param {string} itemId ID of the project
 * @param {Map} statuses Current status map
 * @returns {Object} Updated status and completion info
 */
export const pollProject = async (itemId, statuses) => {
  const response = await api.getProject(itemId);
  const currentStatus = statuses.get(itemId);
  
  // Log the received status for debugging
  console.log(`[PollingContext] pollProject(${itemId}): Received status = ${response?.status}, progress = ${response?.progress}`);
  
  // Enhanced debug logging for comparing status changes
  if (currentStatus) {
    console.log(`[PollingContext] pollProject(${itemId}): Current status = ${currentStatus?.status}, progress = ${currentStatus?.progress}`);
  }
  
  // Check if status or associated data actually changed
  const updated = currentStatus?.status !== response.status || 
                  currentStatus?.progress !== response.progress;
  
  // Log whether an update was detected
  console.log(`[PollingContext] pollProject(${itemId}): Status updated = ${updated}`);
  
  const status = {
    status: response.status,
    progress: response.progress || 0,
    current_step: response.current_step || 'Processing...',
    data: response
  };
  
  // Determine if item is complete
  const isComplete = !PROJECT_PROCESSING_STATUSES.includes(response.status);
  
  // Log the completion status for debugging
  console.log(`[PollingContext] pollProject(${itemId}): isComplete = ${isComplete}`);
  
  // Notify subscribers if updated
  if (updated) {
    const subscriberStatus = {
      status: response.status,
      progress: response.progress || 0,
      current_step: response.current_step || 'Processing...'
    };
    
    console.log(`[PollingContext] pollProject(${itemId}): Notifying subscribers with:`, subscriberStatus);
    notifySubscribers(itemId, subscriberStatus);
  }
  
  return { 
    status, 
    updated, 
    isComplete, 
    finalStatus: response.status 
  };
};

/**
 * Perform initial poll for an item to get initial status
 * @param {string} itemId ID of the item
 * @param {string} itemType Type of polling item
 * @param {string} projectId ID of the project (if applicable)
 * @param {Function} setStatuses Function to update statuses
 * @param {Function} unregisterPolling Function to unregister polling if complete
 */
export const performInitialPoll = async (itemId, itemType, projectId, setStatuses, unregisterPolling) => {
  try {
    let response;
    
    if (itemType === POLLING_TYPES.EXTRACT) {
      if (!projectId) {
        throw new Error('Missing projectId for extraction polling');
      }
      
      response = await api.getShort(projectId, itemId);
      
      setStatuses(prev => {
        const newStatuses = new Map(prev);
        newStatuses.set(itemId, {
          status: response.status,
          progress: response.progress || 0,
          current_step: response.current_step || 'Processing...',
          data: response
        });
        return newStatuses;
      });
      
      // Notify subscribers
      const status = {
        status: response.status,
        progress: response.progress || 0,
        current_step: response.current_step || 'Processing...',
      };
      notifySubscribers(itemId, status);
      
      // Check if already complete
      if (!SEGMENT_PROCESSING_STATUSES.includes(response.status)) {
        console.log(`[PollingContext] Initial poll shows item ${itemId} already complete with status ${response.status}`);
        unregisterPolling(itemId);
      }
    } else if (itemType === POLLING_TYPES.RENDER) {
      if (!projectId) {
        throw new Error('Missing projectId for render polling');
      }
      
      response = await api.getShortDetails(projectId, itemId);
      
      setStatuses(prev => {
        const newStatuses = new Map(prev);
        newStatuses.set(itemId, {
          status: response.status,
          progress: response.progress || 0,
          current_step: response.current_step || 'Rendering...',
          data: response
        });
        return newStatuses;
      });
      
      // Notify subscribers
      const status = {
        status: response.status,
        progress: response.progress || 0,
        current_step: response.current_step || 'Rendering...',
      };
      notifySubscribers(itemId, status);
      
      // Check if already complete
      if (response.status && SEGMENT_FINAL_RENDER_STATUSES.includes(response.status.toUpperCase())) {
        console.log(`[PollingContext] Initial poll shows item ${itemId} already complete with status ${response.status}`);
        unregisterPolling(itemId);
      }
    } else if (itemType === POLLING_TYPES.PROJECT) {
      // For projects, the itemId is the projectId
      response = await api.getProject(itemId);
      
      setStatuses(prev => {
        const newStatuses = new Map(prev);
        newStatuses.set(itemId, {
          status: response.status,
          progress: response.progress || 0,
          current_step: response.current_step || 'Processing...',
          data: response
        });
        return newStatuses;
      });
      
      // Notify subscribers
      const status = {
        status: response.status,
        progress: response.progress || 0,
        current_step: response.current_step || 'Processing...'
      };
      notifySubscribers(itemId, status);
      
      // Check if already complete
      if (!PROJECT_PROCESSING_STATUSES.includes(response.status)) {
        console.log(`[PollingContext] Initial poll shows project ${itemId} already complete with status ${response.status}`);
        unregisterPolling(itemId);
      }
    }
  } catch (error) {
    console.error(`[PollingContext] Error in initial poll for ${itemId}:`, error);
  }
}; 